import { useCallback } from 'react';
import { Serializer } from '@unite-us/client-utils';
import callOrLog from 'common/utils/callOrLog';
import { REFERRAL } from 'common/utils/EventTracker/utils/eventConstants';
import { deselected } from '../../ReferralFormFields/utils/selectField';
import useSensitiveErrorCheck from './useSensitiveErrorCheck';
import useDeselectHandler from './useDeselectHandler';
import useProgramSelect from './useProgramSelect';
import {
  handleDeselectedGroup,
  syncSelectedPrograms,
} from './programSelectHelpers';

export default function useFieldCallbacks({
  canPaginateNetworkGroups,
  caseReferrals,
  componentIndex,
  contact,
  currentUserGroup,
  eventTracker,
  feeScheduleProgramId,
  groupKey,
  referral,
  removeSelectedBrowseGroup,
  selectedFields,
  selectedProgramsContext,
  setFeeScheduleProgramId,
  setHasSensitiveErrors,
  suggestedGroups,
}) {
  const checkSensitiveErrors = useSensitiveErrorCheck({
    caseReferrals,
    componentIndex,
    currentUserGroup,
    groupKey,
    referral,
    selectedFields,
    setHasSensitiveErrors,
    suggestedGroups,
  });

  const { onSelect, onDeselect } = useDeselectHandler({
    canPaginateNetworkGroups,
    feeScheduleProgramId,
    removeSelectedBrowseGroup,
    selectedFields,
    setFeeScheduleProgramId,
  });

  const { onProgramSelect } = useProgramSelect({
    feeScheduleProgramId,
    selectedFields,
    setFeeScheduleProgramId,
  });

  const onProgramGroupSelect = useCallback((selected, index) => {
    const {
      dispatchRemoveProgram,
      dispatchRemoveAllPrograms,
      dispatchAddProgram,
      selectedPrograms,
    } = selectedProgramsContext;

    if (deselected(selected)) {
      handleDeselectedGroup(selectedFields, index, { selectedPrograms, dispatchRemoveProgram, onDeselect });
    } else {
      syncSelectedPrograms(selectedFields, dispatchRemoveAllPrograms, dispatchAddProgram, onSelect);
    }
    checkSensitiveErrors();
  }, [checkSensitiveErrors, onDeselect, onSelect, selectedFields, selectedProgramsContext]);

  const addGroupField = useCallback(() => {
    const eventPayload = Serializer.build({ contact });
    selectedFields.addField();
    checkSensitiveErrors();

    callOrLog(() => eventTracker(REFERRAL.addAnotherRecipient, { contact: eventPayload }));
  }, [checkSensitiveErrors, contact, eventTracker, selectedFields]);

  return {
    checkSensitiveErrors,
    onProgramGroupSelect,
    onProgramSelect,
    addGroupField,
  };
}
